import type { FloorMapCustomDevicePreference, FloorMapDevicePreset, TabletPreviewLayout } from '@/@type/floormap';

export const MOBILE_LEFT_PANEL_WIDTH = 350;
export const MOBILE_HEADER_HEIGHT = 66;
export const DEFAULT_FLOOR_MAP_DEVICE_KEY = 'ipad-air-11-m4';

const defineDevice = (
    key: string,
    label: string,
    family: FloorMapDevicePreset['family'],
    displayInches: number,
    physicalWidth: number,
    physicalHeight: number,
    scale: number,
    releaseYear: number
): FloorMapDevicePreset => ({
    key,
    label,
    family,
    displayInches,
    physicalWidth,
    physicalHeight,
    scale,
    screenWidth: physicalWidth / scale,
    screenHeight: physicalHeight / scale,
    safeTop: 0,
    safeBottom: 0,
    releaseYear
});

export const FLOOR_MAP_DEVICE_PRESETS: FloorMapDevicePreset[] = [
    defineDevice('ipad-1', 'iPad (1st generation)', 'iPad', 9.7, 1024, 768, 1, 2010),
    defineDevice('ipad-2', 'iPad 2', 'iPad', 9.7, 1024, 768, 1, 2011),
    defineDevice('ipad-3', 'iPad (3rd generation)', 'iPad', 9.7, 2048, 1536, 2, 2012),
    defineDevice('ipad-4', 'iPad (4th generation)', 'iPad', 9.7, 2048, 1536, 2, 2012),
    defineDevice('ipad-5', 'iPad (5th generation)', 'iPad', 9.7, 2048, 1536, 2, 2017),
    defineDevice('ipad-6', 'iPad (6th generation)', 'iPad', 9.7, 2048, 1536, 2, 2018),
    defineDevice('ipad-7', 'iPad (7th generation)', 'iPad', 10.2, 2160, 1620, 2, 2019),
    defineDevice('ipad-8', 'iPad (8th generation)', 'iPad', 10.2, 2160, 1620, 2, 2020),
    defineDevice('ipad-9', 'iPad (9th generation)', 'iPad', 10.2, 2160, 1620, 2, 2021),
    defineDevice('ipad-10', 'iPad (10th generation)', 'iPad', 10.86, 2360, 1640, 2, 2022),
    defineDevice('ipad-a16', 'iPad (A16)', 'iPad', 10.86, 2360, 1640, 2, 2025),
    {
        ...defineDevice('ipad-mini-1', 'iPad mini (1st generation)', 'iPad mini', 7.9, 1024, 768, 1, 2012),
        safeTop: 0
    },
    defineDevice('ipad-mini-2', 'iPad mini 2', 'iPad mini', 7.9, 2048, 1536, 2, 2013),
    defineDevice('ipad-mini-3', 'iPad mini 3', 'iPad mini', 7.9, 2048, 1536, 2, 2014),
    defineDevice('ipad-mini-4', 'iPad mini 4', 'iPad mini', 7.9, 2048, 1536, 2, 2015),
    defineDevice('ipad-mini-5', 'iPad mini (5th generation)', 'iPad mini', 7.9, 2048, 1536, 2, 2019),
    defineDevice('ipad-mini-6', 'iPad mini (6th generation)', 'iPad mini', 8.3, 2266, 1488, 2, 2021),
    defineDevice('ipad-mini-a17-pro', 'iPad mini (A17 Pro)', 'iPad mini', 8.3, 2266, 1488, 2, 2024),
    defineDevice('ipad-air-1', 'iPad Air', 'iPad Air', 9.7, 2048, 1536, 2, 2013),
    defineDevice('ipad-air-2', 'iPad Air 2', 'iPad Air', 9.7, 2048, 1536, 2, 2014),
    defineDevice('ipad-air-3', 'iPad Air (3rd generation)', 'iPad Air', 10.5, 2224, 1668, 2, 2019),
    defineDevice('ipad-air-4', 'iPad Air (4th generation)', 'iPad Air', 10.86, 2360, 1640, 2, 2020),
    defineDevice('ipad-air-5', 'iPad Air (5th generation)', 'iPad Air', 10.86, 2360, 1640, 2, 2022),
    defineDevice('ipad-air-11-m2', 'iPad Air 11-inch (M2)', 'iPad Air', 10.86, 2360, 1640, 2, 2024),
    defineDevice('ipad-air-13-m2', 'iPad Air 13-inch (M2)', 'iPad Air', 12.9, 2732, 2048, 2, 2024),
    defineDevice('ipad-air-11-m3', 'iPad Air 11-inch (M3)', 'iPad Air', 10.86, 2360, 1640, 2, 2025),
    defineDevice('ipad-air-13-m3', 'iPad Air 13-inch (M3)', 'iPad Air', 12.9, 2732, 2048, 2, 2025),
    defineDevice('ipad-air-11-m4', 'iPad Air 11-inch (M4)', 'iPad Air', 10.86, 2360, 1640, 2, 2026),
    defineDevice('ipad-air-13-m4', 'iPad Air 13-inch (M4)', 'iPad Air', 12.9, 2732, 2048, 2, 2026),
    defineDevice('ipad-pro-9-7', 'iPad Pro 9.7-inch', 'iPad Pro', 9.7, 2048, 1536, 2, 2016),
    defineDevice('ipad-pro-10-5', 'iPad Pro 10.5-inch', 'iPad Pro', 10.5, 2224, 1668, 2, 2017),
    defineDevice('ipad-pro-11-1', 'iPad Pro 11-inch (1st generation)', 'iPad Pro', 11, 2388, 1668, 2, 2018),
    defineDevice('ipad-pro-11-2', 'iPad Pro 11-inch (2nd generation)', 'iPad Pro', 11, 2388, 1668, 2, 2020),
    defineDevice('ipad-pro-11-3', 'iPad Pro 11-inch (3rd generation)', 'iPad Pro', 11, 2388, 1668, 2, 2021),
    defineDevice('ipad-pro-11-4', 'iPad Pro 11-inch (4th generation)', 'iPad Pro', 11, 2388, 1668, 2, 2022),
    defineDevice('ipad-pro-11-m4', 'iPad Pro 11-inch (M4)', 'iPad Pro', 11, 2420, 1668, 2, 2024),
    defineDevice('ipad-pro-11-m5', 'iPad Pro 11-inch (M5)', 'iPad Pro', 11, 2420, 1668, 2, 2025),
    defineDevice('ipad-pro-12-9-1', 'iPad Pro 12.9-inch (1st generation)', 'iPad Pro', 12.9, 2732, 2048, 2, 2015),
    defineDevice('ipad-pro-12-9-2', 'iPad Pro 12.9-inch (2nd generation)', 'iPad Pro', 12.9, 2732, 2048, 2, 2017),
    defineDevice('ipad-pro-12-9-3', 'iPad Pro 12.9-inch (3rd generation)', 'iPad Pro', 12.9, 2732, 2048, 2, 2018),
    defineDevice('ipad-pro-12-9-4', 'iPad Pro 12.9-inch (4th generation)', 'iPad Pro', 12.9, 2732, 2048, 2, 2020),
    defineDevice('ipad-pro-12-9-5', 'iPad Pro 12.9-inch (5th generation)', 'iPad Pro', 12.9, 2732, 2048, 2, 2021),
    defineDevice('ipad-pro-12-9-6', 'iPad Pro 12.9-inch (6th generation)', 'iPad Pro', 12.9, 2732, 2048, 2, 2022),
    defineDevice('ipad-pro-13-m4', 'iPad Pro 13-inch (M4)', 'iPad Pro', 13, 2752, 2064, 2, 2024),
    defineDevice('ipad-pro-13-m5', 'iPad Pro 13-inch (M5)', 'iPad Pro', 13, 2752, 2064, 2, 2025)
];

export const isFloorMapDeviceKey = (deviceKey: string): boolean => deviceKey === 'custom' || FLOOR_MAP_DEVICE_PRESETS.some((preset) => preset.key === deviceKey);

const isFiniteNumber = (value: number): boolean => Number.isFinite(value);

export const isValidFloorMapCustomDevicePreference = (customDevice: FloorMapCustomDevicePreference): boolean =>
    isFiniteNumber(customDevice.screenWidth) &&
    customDevice.screenWidth > 0 &&
    isFiniteNumber(customDevice.screenHeight) &&
    customDevice.screenHeight > 0 &&
    isFiniteNumber(customDevice.safeTop) &&
    customDevice.safeTop >= 0 &&
    isFiniteNumber(customDevice.safeBottom) &&
    customDevice.safeBottom >= 0;

export const getFloorMapViewportSize = (device: FloorMapDevicePreset): { width: number; height: number } => ({
    width: getTabletPreviewLayout(device).floorMap.width,
    height: getTabletPreviewLayout(device).floorMap.height
});

export const getTabletPreviewLayout = (device: FloorMapDevicePreset): TabletPreviewLayout => {
    const contentHeight = Math.max(device.screenHeight - device.safeTop - device.safeBottom, 0);
    const rightPanelWidth = Math.max(device.screenWidth - MOBILE_LEFT_PANEL_WIDTH, 0);
    const floorMapHeight = Math.max(contentHeight - MOBILE_HEADER_HEIGHT, 0);

    return {
        screen: {
            width: device.screenWidth,
            height: device.screenHeight
        },
        content: {
            width: device.screenWidth,
            height: contentHeight
        },
        leftPanel: {
            width: MOBILE_LEFT_PANEL_WIDTH,
            height: contentHeight
        },
        rightPanel: {
            width: rightPanelWidth,
            height: contentHeight
        },
        header: {
            width: rightPanelWidth,
            height: MOBILE_HEADER_HEIGHT
        },
        floorMap: {
            width: rightPanelWidth,
            height: floorMapHeight
        }
    };
};
